/**
 * The order of Bingkai Kenangan's screens before the camera, read from the
 * project's Navigation settings rather than fixed in each page.
 *
 * The Studio's "Start with payment" (`pages.welcome.settings.startWithPayment`)
 * decides whether the guest pays first or chooses the cut first:
 *
 *   on   welcome → payment → cut (checkout) → filter → capture …
 *   off  welcome → cut (checkout) → payment → filter → capture …
 *
 * Off is the one that prices by cut (the payment screen charges the chosen
 * cut's package), and is what both BK projects run.
 *
 * A page switched off in the Studio is stepped over here, in BK's own order.
 * View.tsx's wrapper also bounces off disabled pages, but its targets follow
 * the stock flow (a disabled payment with pay-after goes to `capture`, which
 * would skip BK's filter screen), so BK pages navigate with this instead.
 */

export type BkStep = 'welcome' | 'checkout' | 'payment' | 'filter' | 'capture'

interface PageSettings {
  settings?: { enabled?: boolean | null; startWithPayment?: boolean | null } | null
}

type Pages = Partial<Record<string, PageSettings | undefined>> | undefined

/** Whether the guest pays before choosing the cut. */
export const paysFirst = (pages: Pages): boolean =>
  pages?.welcome?.settings?.startWithPayment === true

/** Every pre-camera screen in this project's order, switched off or not. */
const orderOf = (pages: Pages): BkStep[] => [
  'welcome',
  ...((paysFirst(pages) ? ['payment', 'checkout'] : ['checkout', 'payment']) as BkStep[]),
  'filter',
  'capture'
]

/**
 * Same test as View.tsx's wrapper: a page is off unless its settings say
 * enabled. Welcome and capture are never skipped.
 */
const isOn = (pages: Pages, step: BkStep): boolean =>
  step === 'welcome' || step === 'capture' || Boolean(pages?.[step]?.settings?.enabled)

/** The pre-camera screens in order, without any the Studio has switched off. */
export const bkSteps = (pages: Pages): BkStep[] => orderOf(pages).filter((s) => isOn(pages, s))

/**
 * The screen after `current` in this project's flow — the next one that is
 * switched on, even when `current` itself is off.
 */
export function nextBkStep(pages: Pages, current: BkStep): BkStep {
  const order = orderOf(pages)
  return order.slice(order.indexOf(current) + 1).find((s) => isOn(pages, s)) ?? 'capture'
}

/**
 * The operator's Skip Payment (the PIN button in ControlButtons), for BK.
 *
 * From the payment screen it goes to the screen after payment in this
 * project's order. From the cut screen there is no cut yet, and the camera
 * needs one, so the booth stays put and remembers the skip: once the cut is
 * chosen, BK's cut screen steps over payment (`takeBkPaymentSkip`). Returns
 * null for "stay here".
 */
export function bkSkipPaymentTarget(pages: Pages, from: 'payment' | 'checkout'): BkStep | null {
  return from === 'payment' ? nextBkStep(pages, 'payment') : null
}

/** A skip asked for on the cut screen, waiting for the cut. One session only. */
let paymentSkipPending = false

export const markBkPaymentSkip = (): void => {
  paymentSkipPending = true
}

/** Whether a skip is waiting, clearing it: a skip is used once. */
export const takeBkPaymentSkip = (): boolean => {
  const pending = paymentSkipPending
  paymentSkipPending = false
  return pending
}
